import { useState, useMemo, useId, useRef, useEffect } from 'react';
import {
  Plus,
  X,
  Calendar,
  ChevronDown,
  ShieldOff,
  Loader2,
  LayoutGrid,
  ListChecks,
  Brush,
  AlertTriangle,
  CheckCircle2,
  Home,
  User,
  Clock,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { UserRole, HousekeepingStatus } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import {
  useTasks,
  useCreateTask,
  useCompleteTask,
  useUpdateTask,
  useUpdateHousekeepingStatus,
  useHousekeepingStaff,
} from '@/hooks/api/useOperations';
import { useProperties } from '@/hooks/api/useProperties';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import type { StaffMember } from '@/services/api/operations.api';

// ---- Type labels (kept local since these are display constants) ----

type TaskType = 'housekeeping' | 'maintenance' | 'inspection' | 'deep_clean' | 'turnover' | 'laundry' | 'minibar' | 'amenity_restock' | 'pest_control' | 'security' | 'guest_request' | 'custom' | 'other';
type TaskPriority = 'urgent' | 'high' | 'medium' | 'low';
type TaskStatus = 'pending' | 'in_progress' | 'completed';

const TASK_TYPE_LABELS: Record<string, string> = {
  housekeeping: 'Housekeeping',
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  deep_clean: 'Deep Clean',
  turnover: 'Turnover',
  laundry: 'Laundry',
  minibar: 'Minibar',
  amenity_restock: 'Amenity Restock',
  pest_control: 'Pest Control',
  security: 'Security',
  guest_request: 'Guest Request',
  other: 'Other',
  custom: 'Custom...',
};
const TASK_TYPE_COLORS: Record<string, string> = {
  housekeeping: 'bg-blue-100 text-blue-700',
  maintenance: 'bg-orange-100 text-orange-700',
  inspection: 'bg-green-100 text-green-700',
  deep_clean: 'bg-purple-100 text-purple-700',
  turnover: 'bg-teal-100 text-teal-700',
  laundry: 'bg-cyan-100 text-cyan-700',
  minibar: 'bg-amber-100 text-amber-700',
  amenity_restock: 'bg-lime-100 text-lime-700',
  pest_control: 'bg-red-100 text-red-700',
  security: 'bg-indigo-100 text-indigo-700',
  guest_request: 'bg-pink-100 text-pink-700',
  other: 'bg-gray-100 text-gray-600',
};
const PRIORITY_LABELS: Record<TaskPriority, string> = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };
const PRIORITY_BADGE_COLORS: Record<TaskPriority, string> = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};
const PRIORITY_DOT_COLORS: Record<TaskPriority, string> = { urgent: 'bg-red-500', high: 'bg-orange-400', medium: 'bg-blue-400', low: 'bg-gray-400' };

const ALLOWED_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.OPERATIONS];
const STATUS_COLUMNS: { key: TaskStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
];
const COLUMN_HEADER_STYLES: Record<TaskStatus, string> = { pending: 'border-yellow-400', in_progress: 'border-blue-400', completed: 'border-green-400' };

const COLUMN_EMPTY_MESSAGES: Record<TaskStatus, string> = {
  pending: 'No pending tasks. Great job staying on top of things!',
  in_progress: 'No tasks in progress right now.',
  completed: 'No completed tasks yet.',
};

// ---- Housekeeping Status Config ----

const HK_CONFIG: Record<HousekeepingStatus, { label: string; bg: string; border: string; badge: string; dot: string }> = {
  [HousekeepingStatus.CLEAN]: { label: 'Clean', bg: 'bg-white', border: 'border-l-green-500', badge: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  [HousekeepingStatus.INSPECTED]: { label: 'Inspected', bg: 'bg-white', border: 'border-l-green-400', badge: 'bg-green-100 text-green-800', dot: 'bg-green-400' },
  [HousekeepingStatus.DIRTY]: { label: 'Dirty', bg: 'bg-red-50', border: 'border-l-red-500', badge: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
  [HousekeepingStatus.OCCUPIED]: { label: 'Occupied', bg: 'bg-blue-50', border: 'border-l-blue-500', badge: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  [HousekeepingStatus.MAINTENANCE]: { label: 'Maintenance', bg: 'bg-gray-50', border: 'border-l-gray-400', badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-400' },
  [HousekeepingStatus.OUT_OF_ORDER]: { label: 'Out of Order', bg: 'bg-gray-50', border: 'border-l-gray-600', badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-600' },
};

// ---- Helpers ----

function fmtDate(iso: string): string {
  try { return format(parseISO(iso), 'MMM d'); } catch { return iso; }
}

function timeSinceCreated(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

// ---- Access Denied ----

function AccessDeniedCard() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-white py-24 px-6 text-center gap-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-50">
        <ShieldOff size={32} className="text-red-400" aria-hidden="true" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-700">Access Denied</h2>
        <p className="mt-1 text-sm text-gray-400 max-w-sm">Operations is restricted to Admins and Operations staff.</p>
      </div>
    </div>
  );
}

// ---- Enhanced Task Card ----

interface TaskCardProps {
  task: Record<string, unknown>;
  staff: StaffMember[];
  onComplete: (id: string) => void;
  onAssign: (id: string) => void;
}

function TaskCard({ task, staff, onComplete, onAssign }: TaskCardProps) {
  const taskType = (task.taskType as TaskType) ?? 'housekeeping';
  const priority = (task.priority as TaskPriority) ?? 'medium';
  const assigneeId = task.assignedTo as string | undefined;
  const assigneeName = assigneeId
    ? staff.find((s) => s.id === assigneeId)?.name ?? (assigneeId as string)
    : undefined;
  const isCompleted = (task.status as string) === 'completed';
  const dueAt = (task.dueAt as string) ?? (task.dueDate as string) ?? undefined;
  const createdAt = task.createdAt as string | undefined;
  const unitName = (task.unitName as string) ?? '';
  const propertyName = (task.propertyName as string) ?? '';

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 p-3.5 shadow-sm hover:shadow-md transition-shadow"
      aria-label={`Task: ${task.title as string}`}
    >
      {/* Title */}
      <h4 className="text-sm font-bold text-gray-900 mb-1.5 line-clamp-2">
        {task.title as string}
      </h4>

      {/* Unit + Property */}
      <div className="flex items-center gap-1 mb-2">
        <Home size={11} className="text-gray-400 shrink-0" aria-hidden="true" />
        <p className="text-xs text-gray-500 truncate">
          {unitName}{propertyName ? ` - ${propertyName}` : ''}
        </p>
      </div>

      {/* Assignee */}
      <div className="flex items-center gap-1 mb-2">
        <User size={11} className="shrink-0" aria-hidden="true" />
        {assigneeName ? (
          <span className="text-xs text-gray-600 truncate">{assigneeName}</span>
        ) : (
          <span className="text-xs font-medium text-orange-600">Unassigned</span>
        )}
      </div>

      {/* Priority badge + Type badge */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={cn(
          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
          PRIORITY_BADGE_COLORS[priority] ?? 'bg-gray-100 text-gray-600',
        )}>
          <span className={cn('w-1.5 h-1.5 rounded-full', PRIORITY_DOT_COLORS[priority])} aria-hidden="true" />
          {PRIORITY_LABELS[priority] ?? priority}
        </span>
        <span className={cn(
          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
          TASK_TYPE_COLORS[taskType] ?? 'bg-gray-100 text-gray-600',
        )}>
          {TASK_TYPE_LABELS[taskType] ?? taskType}
        </span>
      </div>

      {/* Due date + Time since created */}
      <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
        <span className="flex items-center gap-1">
          <Calendar size={10} aria-hidden="true" />
          {dueAt ? fmtDate(dueAt) : 'No due date'}
        </span>
        {createdAt && (
          <span className="flex items-center gap-1">
            <Clock size={10} aria-hidden="true" />
            {timeSinceCreated(createdAt)}
          </span>
        )}
      </div>

      {/* Quick action buttons */}
      {!isCompleted && (
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={() => onComplete(task.id as string)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
          >
            <CheckCircle2 size={12} aria-hidden="true" />
            Complete
          </button>
          {!assigneeName && (
            <button
              type="button"
              onClick={() => onAssign(task.id as string)}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <User size={12} aria-hidden="true" />
              Assign
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Kanban Column ----

interface KanbanColumnProps {
  label: string;
  status: TaskStatus;
  tasks: Array<Record<string, unknown>>;
  staff: StaffMember[];
  onComplete: (id: string) => void;
  onAssign: (id: string) => void;
  onAddTask: () => void;
}

function KanbanColumn({ label, status, tasks, staff, onComplete, onAssign, onAddTask }: KanbanColumnProps) {
  return (
    <div className="flex flex-col min-w-[300px] flex-1">
      <div className={cn('flex items-center justify-between px-3 py-2.5 rounded-t-lg border-t-4 bg-gray-50', COLUMN_HEADER_STYLES[status])}>
        <h3 className="text-sm font-semibold text-gray-700">{label}</h3>
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-xs font-bold text-gray-600">{tasks.length}</span>
      </div>
      <div className="flex flex-col gap-2 p-2 bg-gray-50/50 rounded-b-lg min-h-[200px] border border-t-0 border-gray-200" role="list" aria-label={`${label} tasks`}>
        {tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center gap-3">
            <p className="text-xs text-gray-400">{COLUMN_EMPTY_MESSAGES[status]}</p>
            <button
              type="button"
              onClick={onAddTask}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <Plus size={14} aria-hidden="true" />
              Add Task
            </button>
          </div>
        ) : (
          tasks.map((task) => (
            <div key={task.id as string} role="listitem">
              <TaskCard
                task={task}
                staff={staff}
                onComplete={onComplete}
                onAssign={onAssign}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ---- Unit Status Card ----

interface UnitStatusCardProps {
  unit: Record<string, unknown>;
  onClick: () => void;
}

function UnitStatusCard({ unit, onClick }: UnitStatusCardProps) {
  const status = (unit.housekeepingStatus as HousekeepingStatus) ?? HousekeepingStatus.CLEAN;
  const cfg = HK_CONFIG[status] ?? HK_CONFIG[HousekeepingStatus.CLEAN];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-xl border border-gray-200 border-l-4 p-3 text-left w-full transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        cfg.bg,
        cfg.border,
      )}
      aria-label={`Unit ${unit.name as string}, status: ${cfg.label}`}
    >
      <div className="flex items-start justify-between gap-1 mb-1.5">
        <span className="font-semibold text-gray-900 text-sm truncate">{unit.name as string}</span>
        <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full shrink-0', cfg.badge)}>
          <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', cfg.dot)} aria-hidden="true" />
          {cfg.label}
        </span>
      </div>
      <p className="text-xs text-gray-400 truncate">{unit.propertyName as string}</p>
    </button>
  );
}

// ---- Update Housekeeping Modal ----

function UpdateHousekeepingModal({
  unit,
  onClose,
}: {
  unit: Record<string, unknown> | null;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<HousekeepingStatus>(
    (unit?.housekeepingStatus as HousekeepingStatus) ?? HousekeepingStatus.CLEAN,
  );
  const updateMutation = useUpdateHousekeepingStatus();
  const titleId = useId();

  useEffect(() => {
    if (unit) setStatus((unit.housekeepingStatus as HousekeepingStatus) ?? HousekeepingStatus.CLEAN);
  }, [unit]);

  if (!unit) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!unit) return;
    updateMutation.mutate({ unitId: unit.id as string, status }, { onSuccess: onClose });
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 id={titleId} className="text-base font-semibold text-gray-900">Update Unit Status</h2>
            <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">{unit.name as string}</p>
              <p className="text-xs text-gray-400">{unit.propertyName as string}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Housekeeping Status</label>
              <div className="grid grid-cols-2 gap-2">
                {(Object.entries(HK_CONFIG) as [HousekeepingStatus, typeof HK_CONFIG[HousekeepingStatus]][]).map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStatus(key)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                      status === key
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
                    )}
                  >
                    <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', cfg.dot)} aria-hidden="true" />
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60"
              >
                {updateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ---- Assign Task Modal ----

function AssignTaskModal({
  taskId,
  staff,
  onClose,
}: {
  taskId: string | null;
  staff: StaffMember[];
  onClose: () => void;
}) {
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const updateTask = useUpdateTask();
  const titleId = useId();

  if (!taskId) return null;

  function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!taskId || !selectedStaffId) return;
    updateTask.mutate(
      { id: taskId, payload: { assignedTo: selectedStaffId } },
      { onSuccess: () => { setSelectedStaffId(''); onClose(); } },
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 id={titleId} className="text-base font-semibold text-gray-900">Assign Task</h2>
            <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100" aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <form onSubmit={handleAssign} className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Staff Member</label>
              <div className="relative">
                <select
                  required
                  value={selectedStaffId}
                  onChange={(e) => setSelectedStaffId(e.target.value)}
                  className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="" disabled>Select a staff member</option>
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50">Cancel</button>
              <button
                type="submit"
                disabled={!selectedStaffId || updateTask.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60"
              >
                {updateTask.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
                Assign
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ---- New Task Modal ----

interface NewTaskFormData {
  unitId: string;
  taskType: TaskType;
  customType: string;
  title: string;
  description: string;
  assignedTo: string;
  priority: TaskPriority;
  dueDate: string;
}

function NewTaskModal({
  isOpen,
  onClose,
  onSubmit,
  units,
  staff,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: NewTaskFormData) => void;
  units: Array<Record<string, unknown>>;
  staff: StaffMember[];
}) {
  const EMPTY_FORM: NewTaskFormData = {
    unitId: (units[0]?.id as string) ?? '',
    taskType: 'housekeeping',
    customType: '',
    title: '',
    description: '',
    assignedTo: '',
    priority: 'medium',
    dueDate: '',
  };

  const [form, setForm] = useState<NewTaskFormData>(EMPTY_FORM);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const titleInputId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen && !dialog.open) dialog.showModal();
    else if (!isOpen && dialog.open) dialog.close();
  }, [isOpen]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.unitId || !form.dueDate) return;
    onSubmit(form);
    setForm(EMPTY_FORM);
  }

  function update<K extends keyof NewTaskFormData>(key: K, value: NewTaskFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  if (!isOpen) return null;

  return (
    <dialog ref={dialogRef} onClose={onClose} className="fixed inset-0 z-50 m-auto w-full max-w-lg rounded-xl bg-white shadow-xl border border-gray-200 p-0 backdrop:bg-black/40" aria-labelledby={titleId}>
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">New Task</h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" aria-label="Close dialog"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Unit <span aria-label="required">*</span></label>
            <div className="relative">
              <select required value={form.unitId} onChange={(e) => update('unitId', e.target.value)} className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                {units.map((u) => (<option key={u.id as string} value={u.id as string}>{(u.propertyName as string) ?? ''} - {(u.name as string) ?? ''}</option>))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Task Type</label>
            <div className="relative">
              <select value={form.taskType} onChange={(e) => update('taskType', e.target.value as TaskType)} className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                {(Object.entries(TASK_TYPE_LABELS) as [string, string][]).map(([val, label]) => (<option key={val} value={val}>{label}</option>))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
            {form.taskType === 'custom' && (
              <input
                type="text"
                placeholder="Enter custom task type"
                value={form.customType}
                onChange={(e) => update('customType', e.target.value)}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            )}
          </div>
          <div>
            <label htmlFor={titleInputId} className="block text-xs font-medium text-gray-600 mb-1">Title <span aria-label="required">*</span></label>
            <input id={titleInputId} type="text" required value={form.title} onChange={(e) => update('title', e.target.value)} placeholder="e.g. Deep clean after checkout" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Optional details..." className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 resize-y placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Assign To</label>
            <div className="relative">
              <select value={form.assignedTo} onChange={(e) => update('assignedTo', e.target.value)} className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                <option value="">Unassigned</option>
                {staff.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
            <div className="relative">
              <select value={form.priority} onChange={(e) => update('priority', e.target.value as TaskPriority)} className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                {(Object.entries(PRIORITY_LABELS) as [TaskPriority, string][]).map(([val, label]) => (<option key={val} value={val}>{label}</option>))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Due Date <span aria-label="required">*</span></label>
            <input type="date" required value={form.dueDate} onChange={(e) => update('dueDate', e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">Cancel</button>
          <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2">Create Task</button>
        </div>
      </form>
    </dialog>
  );
}

// ---- Unit Status Tab ----

function UnitStatusTab({ properties }: { properties: Array<Record<string, unknown>> }) {
  const [selectedUnit, setSelectedUnit] = useState<Record<string, unknown> | null>(null);

  const allUnits = useMemo(() =>
    properties.flatMap((p) => {
      const rawUnits = Array.isArray(p.units) ? (p.units as Array<Record<string, unknown>>) : [];
      return rawUnits.map((u): Record<string, unknown> => ({ ...u, propertyName: p.name, propertyId: p.id }));
    }),
    [properties],
  );

  // Stats
  const toClean = allUnits.filter((u) => (u['housekeepingStatus'] as string) === HousekeepingStatus.DIRTY).length;
  const outOfOrder = allUnits.filter((u) => (u['housekeepingStatus'] as string) === HousekeepingStatus.OUT_OF_ORDER || (u['housekeepingStatus'] as string) === HousekeepingStatus.MAINTENANCE).length;
  const clean = allUnits.filter((u) => (u['housekeepingStatus'] as string) === HousekeepingStatus.CLEAN || (u['housekeepingStatus'] as string) === HousekeepingStatus.INSPECTED).length;
  const occupied = allUnits.filter((u) => (u['housekeepingStatus'] as string) === HousekeepingStatus.OCCUPIED).length;

  return (
    <div className="space-y-5">
      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'To Clean', value: toClean, icon: <Brush size={18} className="text-red-500" />, bg: 'bg-red-50' },
          { label: 'Occupied', value: occupied, icon: <Home size={18} className="text-blue-500" />, bg: 'bg-blue-50' },
          { label: 'Clean', value: clean, icon: <CheckCircle2 size={18} className="text-green-500" />, bg: 'bg-green-50' },
          { label: 'Out of Order', value: outOfOrder, icon: <AlertTriangle size={18} className="text-gray-500" />, bg: 'bg-gray-50' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
            <div className={cn('flex items-center justify-center w-9 h-9 rounded-lg shrink-0', s.bg)}>
              {s.icon}
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900 leading-none">{s.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Unit grid grouped by property */}
      {properties.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <p className="text-sm text-gray-400">No properties configured.</p>
        </div>
      ) : (
        properties.map((property) => {
          const propUnits = Array.isArray(property.units) ? (property.units as Array<Record<string, unknown>>) : [];
          const enrichedUnits: Array<Record<string, unknown>> = propUnits.map((u) => ({ ...u, propertyName: property.name, propertyId: property.id }));
          return (
            <div key={property.id as string} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
                <Home size={16} className="text-gray-400" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-gray-900">{property.name as string}</h3>
                <span className="text-xs text-gray-400">({propUnits.length} units)</span>
              </div>
              {propUnits.length === 0 ? (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm text-gray-400">No units in this property.</p>
                </div>
              ) : (
                <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {enrichedUnits.map((unit) => (
                    <UnitStatusCard
                      key={unit['id'] as string}
                      unit={unit}
                      onClick={() => setSelectedUnit(unit)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Update modal */}
      <UpdateHousekeepingModal unit={selectedUnit} onClose={() => setSelectedUnit(null)} />
    </div>
  );
}

// ---- Tasks Tab ----

function TasksTab({
  properties,
  units,
  hasAccess,
}: {
  properties: Array<Record<string, unknown>>;
  units: Array<Record<string, unknown>>;
  hasAccess: boolean;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterType, setFilterType] = useState<TaskType | ''>('');
  const [filterPriority, setFilterPriority] = useState<TaskPriority | ''>('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [assigningTaskId, setAssigningTaskId] = useState<string | null>(null);

  const filterTypeId = useId();
  const filterPriorityId = useId();
  const filterPropertyId = useId();
  const filterAssigneeId = useId();

  const selectedPropertyId = useSelectedPropertyId();

  const { data: tasksData, isLoading } = useTasks(selectedPropertyId ? { propertyId: selectedPropertyId } : undefined);
  const createTaskMutation = useCreateTask();
  const completeTaskMutation = useCompleteTask();
  const { data: staffData } = useHousekeepingStaff(selectedPropertyId || undefined);

  const staff: StaffMember[] = useMemo(() => {
    if (!staffData) return [];
    return Array.isArray(staffData) ? staffData : [];
  }, [staffData]);

  const tasks: Array<Record<string, unknown>> = useMemo(() => {
    if (!tasksData) return [];
    return Array.isArray(tasksData) ? tasksData : (tasksData.data ?? []);
  }, [tasksData]);

  // Collect unique assignee IDs for filter dropdown
  const assigneeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const t of tasks) {
      const id = t.assignedTo as string;
      if (id && !seen.has(id)) {
        const staffMember = staff.find((s) => s.id === id);
        seen.set(id, staffMember?.name ?? id);
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [tasks, staff]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (filterType && t.taskType !== filterType) return false;
      if (filterPriority && t.priority !== filterPriority) return false;
      if (filterProperty && t.propertyId !== filterProperty) return false;
      if (filterAssignee) {
        if (filterAssignee === '__unassigned__') {
          if (t.assignedTo) return false;
        } else if (t.assignedTo !== filterAssignee) {
          return false;
        }
      }
      return true;
    });
  }, [tasks, filterType, filterPriority, filterProperty, filterAssignee]);

  const tasksByStatus = useMemo(() => {
    const map: Record<TaskStatus, Array<Record<string, unknown>>> = { pending: [], in_progress: [], completed: [] };
    for (const t of filteredTasks) {
      const status = (t.status as TaskStatus) ?? 'pending';
      if (map[status]) map[status].push(t);
    }
    return map;
  }, [filteredTasks]);

  function handleNewTask(data: NewTaskFormData) {
    const resolvedType = data.taskType === 'custom' ? (data.customType?.trim() || 'other') : data.taskType;
    createTaskMutation.mutate({
      unitId: data.unitId,
      taskType: resolvedType,
      title: data.title,
      description: data.description || undefined,
      assignedTo: data.assignedTo || null,
      priority: data.priority,
      dueAt: data.dueDate || null,
    });
    setIsModalOpen(false);
  }

  function handleComplete(taskId: string) {
    completeTaskMutation.mutate(taskId);
  }

  function handleAssign(taskId: string) {
    setAssigningTaskId(taskId);
  }

  // Stats for tasks tab
  const pendingCount = tasksByStatus.pending.length;
  const inProgressCount = tasksByStatus.in_progress.length;
  const completedCount = tasksByStatus.completed.length;

  return (
    <div className="space-y-5">
      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Pending', value: pendingCount, color: 'bg-yellow-50', textColor: 'text-yellow-600' },
          { label: 'In Progress', value: inProgressCount, color: 'bg-blue-50', textColor: 'text-blue-600' },
          { label: 'Completed Today', value: completedCount, color: 'bg-green-50', textColor: 'text-green-600' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 text-center">
            <p className={cn('text-2xl font-bold leading-none', s.textColor)}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters + New Task */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor={filterPropertyId} className="block text-xs font-medium text-gray-600 mb-1">Property</label>
            <div className="relative">
              <select id={filterPropertyId} value={filterProperty} onChange={(e) => setFilterProperty(e.target.value)} className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                <option value="">All Properties</option>
                {properties.map((p) => (<option key={p.id as string} value={p.id as string}>{p.name as string}</option>))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
          </div>
          <div>
            <label htmlFor={filterAssigneeId} className="block text-xs font-medium text-gray-600 mb-1">Assignee</label>
            <div className="relative">
              <select id={filterAssigneeId} value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                <option value="">All Assignees</option>
                <option value="__unassigned__">Unassigned</option>
                {assigneeOptions.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
          </div>
          <div>
            <label htmlFor={filterPriorityId} className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
            <div className="relative">
              <select id={filterPriorityId} value={filterPriority} onChange={(e) => setFilterPriority(e.target.value as TaskPriority | '')} className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                <option value="">All Priorities</option>
                {(Object.entries(PRIORITY_LABELS) as [TaskPriority, string][]).map(([val, label]) => (<option key={val} value={val}>{label}</option>))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
          </div>
          <div>
            <label htmlFor={filterTypeId} className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <div className="relative">
              <select id={filterTypeId} value={filterType} onChange={(e) => setFilterType(e.target.value as TaskType | '')} className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                <option value="">All Types</option>
                {(Object.entries(TASK_TYPE_LABELS) as [string, string][]).filter(([val]) => val !== 'custom').map(([val, label]) => (<option key={val} value={val}>{label}</option>))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
          </div>
        </div>
        {hasAccess && (
          <button type="button" onClick={() => setIsModalOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2">
            <Plus size={16} aria-hidden="true" />
            New Task
          </button>
        )}
      </div>

      <p className="sr-only" aria-live="polite">{filteredTasks.length} {filteredTasks.length === 1 ? 'task' : 'tasks'} shown</p>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
          <span className="ml-2 text-sm text-gray-500">Loading tasks...</span>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {STATUS_COLUMNS.map(({ key, label }) => (
            <KanbanColumn
              key={key}
              label={label}
              status={key}
              tasks={tasksByStatus[key]}
              staff={staff}
              onComplete={handleComplete}
              onAssign={handleAssign}
              onAddTask={() => setIsModalOpen(true)}
            />
          ))}
        </div>
      )}

      <NewTaskModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleNewTask} units={units} staff={staff} />
      <AssignTaskModal taskId={assigningTaskId} staff={staff} onClose={() => setAssigningTaskId(null)} />
    </div>
  );
}

// ---- Page ----

type OperationsTab = 'unit-status' | 'tasks';

export function OperationsPage() {
  const { user } = useAuth();
  const hasAccess = user?.role && ALLOWED_ROLES.includes(user.role as UserRole);
  const [activeTab, setActiveTab] = useState<OperationsTab>('unit-status');

  const { data: propertiesData, isLoading: propertiesLoading } = useProperties();

  const properties: Array<Record<string, unknown>> = useMemo(() => {
    if (!propertiesData) return [];
    return Array.isArray(propertiesData) ? propertiesData : (propertiesData.data ?? []);
  }, [propertiesData]);

  const units: Array<Record<string, unknown>> = useMemo(() => {
    return properties.flatMap((p: Record<string, unknown>) => {
      const propUnits = Array.isArray(p.units) ? p.units : [];
      return propUnits.map((u: Record<string, unknown>) => ({ ...u, propertyName: p.name, propertyId: p.id }));
    });
  }, [properties]);

  const TABS: { id: OperationsTab; label: string; icon: React.ReactNode }[] = [
    { id: 'unit-status', label: 'Unit Status', icon: <LayoutGrid size={15} /> },
    { id: 'tasks', label: 'Tasks', icon: <ListChecks size={15} /> },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-0">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Operations</h1>
          <p className="text-sm text-gray-500 mt-0.5">Housekeeping status and task management</p>
        </div>
      </div>

      {!hasAccess ? (
        <AccessDeniedCard />
      ) : (
        <>
          {/* Tab navigation */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700',
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {propertiesLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-primary-600" />
            </div>
          ) : (
            <>
              {activeTab === 'unit-status' && <UnitStatusTab properties={properties} />}
              {activeTab === 'tasks' && <TasksTab properties={properties} units={units} hasAccess={hasAccess} />}
            </>
          )}
        </>
      )}
    </div>
  );
}
